'use client';

import { useEffect, useRef } from 'react';

import { useAuth } from '@/contexts/AuthContext';
import { identifyUser, resetAnalyticsIdentity } from '@/lib/analytics';

/**
 * Bridges the auth session to the PostHog identity (FRE-1261).
 *
 * Mounted once from `app/providers.tsx` (must sit inside `AuthProvider`).
 * When the authenticated user resolves, `identifyUser(user.id)` aliases the
 * anonymous device UUID to the person distinct_id so feature-flag bucketing
 * and person-targeted release conditions follow the account instead of the
 * device. Consent gating is unchanged: `identifyUser` retains the id and
 * `lib/analytics` applies it only after the consent-gated PostHog init
 * (FRE-809 — no consent → no PostHog at all).
 *
 * When the user transitions signed-in → signed-out *within* a page life
 * (e.g. `setUser(null)`), the identity is reset so a shared device doesn't
 * keep attributing events to the previous account. The common logout path
 * hard-navigates before effects re-run, so `AuthContextRTK.logout()` and
 * `sanitizeInvalidSession()` call `resetAnalyticsIdentity()` themselves.
 */
export function AnalyticsIdentityBridge(): null {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  // Deliberately a previous-value ref instead of an effect cleanup: a cleanup
  // would also fire reset on unmount and on React strict-mode's dev
  // mount→unmount→mount cycle, churning the anonymous distinct_id (each
  // `posthog.reset()` regenerates it and reloads flags) without a real
  // sign-out. The ref only resets on an actual signed-in → signed-out
  // transition.
  const previousUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    if (userId) {
      // Direct account switch (user A → user B with no intervening sign-out,
      // e.g. via `setUser`): drop A's identity first so B doesn't inherit A's
      // person state or feature-flag buckets.
      if (previousUserId && previousUserId !== userId) {
        resetAnalyticsIdentity();
      }
      identifyUser(userId);
    } else if (previousUserId) {
      resetAnalyticsIdentity();
    }
    previousUserIdRef.current = userId;
  }, [userId]);

  return null;
}
